import Parse from "parse";
import { useEffect, useMemo, useState } from "react";
import {
  SIV_DeliveryNotes,
  SIV_DeliveryNotesPosition,
  SIV_GoodsReceiptProcessPosition,
  SIV_GoodsReceiving,
  SIV_GoodsReceivingItem,
  SIV_Order,
  SIV_Package,
  SIV_PackageItem,
} from "../../../parsegenerated";

export const useGoodsReceivings = (order?: SIV_Order | null) => {
  const [goodsReceivings, setGoodsReceivings] = useState<SIV_GoodsReceiving[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!order) {
      setGoodsReceivings([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_GoodsReceiving);
        query.equalTo("order", order);
        query.include("deliveryNote");
        query.include("tenant");
        query.descending("date");
        const results = await query.find();
        setGoodsReceivings(results);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [order?.id]);

  return { goodsReceivings, loading, error };
};

export const useGoodsReceivingItems = (
  goodsReceiving?: SIV_GoodsReceiving | null,
) => {
  const [items, setItems] = useState<SIV_GoodsReceivingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!goodsReceiving) {
      setItems([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_GoodsReceivingItem);
        query.equalTo("goodsReceiving", goodsReceiving);
        query.include("tenant");
        query.include("goodsReceiptProcessPosition");
        // nested include not in TS types but valid at runtime
        (query as any).include("goodsReceiptProcessPosition.goodsReceiptProcess");
        query.ascending("itemNo");
        const results = await query.find();
        setItems(results);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [goodsReceiving?.id]);

  return { items, loading, error };
};

type EnrichmentEntry = {
  packages: SIV_Package[];
  deliveryNotes: SIV_DeliveryNotes[];
};

export const useGoodsReceivingItemEnrichment = (
  items: SIV_GoodsReceivingItem[],
) => {
  const [packageItems, setPackageItems] = useState<SIV_PackageItem[]>([]);
  const [deliveryNotePositions, setDeliveryNotePositions] = useState<
    SIV_DeliveryNotesPosition[]
  >([]);
  const [loading, setLoading] = useState(false);

  const itemsKey = items.map((i) => i.id).join(",");

  useEffect(() => {
    const positions = items
      .map((i) => i.goodsReceiptProcessPosition)
      .filter((p): p is SIV_GoodsReceiptProcessPosition => p != null);

    if (positions.length === 0) {
      setPackageItems([]);
      setDeliveryNotePositions([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const pkgQuery = new Parse.Query(SIV_PackageItem);
        pkgQuery.containedIn("goodsReceiptProcessPosition", positions);
        pkgQuery.include("package");

        const dlvQuery = new Parse.Query(SIV_DeliveryNotesPosition);
        dlvQuery.containedIn("goodsReceiptProcessPosition", positions);
        dlvQuery.include("deliveryNote");

        const [pkgItems, dlvPos] = await Promise.all([
          pkgQuery.find(),
          dlvQuery.find(),
        ]);
        setPackageItems(pkgItems);
        setDeliveryNotePositions(dlvPos);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [itemsKey]);

  const enrichmentByPositionId = useMemo(() => {
    const map = new Map<string, EnrichmentEntry>();

    for (const pi of packageItems) {
      const posId = pi.goodsReceiptProcessPosition?.id;
      if (!posId) continue;
      if (!map.has(posId)) map.set(posId, { packages: [], deliveryNotes: [] });
      map.get(posId)!.packages.push(pi.package);
    }

    for (const dp of deliveryNotePositions) {
      const posId = dp.goodsReceiptProcessPosition?.id;
      if (!posId || !dp.deliveryNote) continue;
      if (!map.has(posId)) map.set(posId, { packages: [], deliveryNotes: [] });
      map.get(posId)!.deliveryNotes.push(dp.deliveryNote);
    }

    return map;
  }, [packageItems, deliveryNotePositions]);

  return { enrichmentByPositionId, loading };
};

export const useGoodsReceiving = (id?: string) => {
  const [goodsReceiving, setGoodsReceiving] =
    useState<SIV_GoodsReceiving | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) {
      setGoodsReceiving(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_GoodsReceiving);
        query.include("order");
        query.include("deliveryNote");
        query.include("tenant");
        const result = await query.get(id);
        setGoodsReceiving(result);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  return { goodsReceiving, loading, error };
};
